import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Form, Input, Select, DatePicker, InputNumber,
  Button, Row, Col, Modal, TimePicker
} from 'antd';
import moment from 'moment';
import ConfirmModal from '../ConfirmModal';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUsersByOrganisation, clearOrganisationUsers } from '../../redux/userOrganisationsSlice';

const { Option } = Select;
const { TextArea } = Input;

const workSchedules = [
  '6/1', '5/2', '4/4', '4/3', '4/2', '3/3', '2/2', '1/3', '1/2',
  'По выходным', 'Свободный', 'Другое'
];

const vacancyReasons = [
  'Новая вакансия',
  'Внутренний перевод',
  'Декрет',
  'Замена'
];

const CreateModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  organisations,
  users
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { organisationUsers } = useSelector((state) => state.userOrganisations);
  const [form] = Form.useForm();
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [isDraftSaved, setIsDraftSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isHiddenVacancy, setIsHiddenVacancy] = useState(false);
  const [businessTripsRequired, setBusinessTripsRequired] = useState(false);
  const [relocationWillingness, setRelocationWillingness] = useState(false);
  const [communicationCompensation, setCommunicationCompensation] = useState(false);
  const [hasCar, setHasCar] = useState(false);

  const carCompensationValue = Form.useWatch('car_compensation', form);
  const driverLicenseRequiredValue = Form.useWatch('driver_license_required', form);
  const isMaterialResponsibleValue = Form.useWatch('is_material_responsible', form);
  const selectedOrganisationId = Form.useWatch('organisation_id', form);

  const initialValuesRef = useRef({});

  const filterDepartments = useCallback((orgId) => {
    const organisation = organisations.find(org => org.organisation_id === orgId);
    if (organisation && organisation.departments) {
      setFilteredDepartments(organisation.departments);
    } else {
      setFilteredDepartments([]);
    }
  }, [organisations]);

  const handleCheckboxToggle = (fieldName) => {
    const currentValue = form.getFieldValue(fieldName) || false;
    form.setFieldsValue({ [fieldName]: !currentValue });
  };

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setIsDraftSaved(false);
      setIsHiddenVacancy(false);
      setBusinessTripsRequired(false);
      setRelocationWillingness(false);
      setCommunicationCompensation(false);
      setHasCar(false);
      dispatch(clearOrganisationUsers());

      setTimeout(() => {
        const firstName = localStorage.getItem('first_name') || '';
        const lastName = localStorage.getItem('last_name') || '';
        const authorName = `${firstName} ${lastName}`.trim();

        if (authorName) {
          form.setFieldsValue({
            author_name: authorName
          });
        }
      }, 0);

      if (initialValues) {
        const mappedValues = mapInitialValuesToForm(initialValues);
        form.setFieldsValue(mappedValues);
        setIsHiddenVacancy(mappedValues.is_hidden || false);
        setBusinessTripsRequired(mappedValues.business_trips_required || false);
        setRelocationWillingness(mappedValues.relocation_willingness || false);
        setCommunicationCompensation(mappedValues.communication_compensation || false);
        setHasCar(mappedValues.has_car || false);

        // Фильтруем отделы для выбранной организации
        if (mappedValues.organisation_id) {
          filterDepartments(mappedValues.organisation_id);
          // Загрузка пользователей происходит автоматически через useEffect при изменении selectedOrganisationId
        }
      } else {
        dispatch(clearOrganisationUsers());
      }
    } else {
      dispatch(clearOrganisationUsers());
    }
  }, [isOpen, initialValues, organisations, form, filterDepartments, dispatch]);

  const checkForChanges = useCallback(() => {
    const currentValues = form.getFieldsValue(true);
    return JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);
  }, [form]);

  const handleFormChange = useCallback(() => {
    setHasUnsavedChanges(checkForChanges());
  }, [checkForChanges]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        initialValuesRef.current = form.getFieldsValue(true);
        setHasUnsavedChanges(false);
      }, 0);
    }
  }, [isOpen, form]);

  const handleModalCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const handleConfirmClose = useCallback((shouldClose) => {
    if (shouldClose) {
      onClose();
    }
    setShowConfirm(false);
  }, [onClose]);

  const getValidationRules = (fieldName, isRequired = false) => {
    const rules = [];
    if (isRequired) {
      rules.push({ required: true, message: 'Это поле обязательно' });
    }

    if (fieldName.includes('salary')) {
      rules.push({
        validator: (_, value) => {
          if (value && value < 0) {
            return Promise.reject(new Error('Зарплата не может быть отрицательной'));
          }
          return Promise.resolve();
        }
      });
    }

    return rules;
  };

  const mapInitialValuesToForm = (values) => {
    const parseSalary = (salary) => {
      if (!salary) return { min: null, max: null };

      if (typeof salary === 'string' && salary.includes('-')) {
        const parts = salary.split('-').map(part => part.trim());
        const min = parseFloat(parts[0]) || null;
        const max = parseFloat(parts[1]) || null;
        return { min, max };
      }

      const numSalary = parseFloat(salary);
      return {
        min: isNaN(numSalary) ? null : numSalary,
        max: isNaN(numSalary) ? null : numSalary
      };
    };

    const probationSalary = parseSalary(values.trial_salary);
    const postProbationSalary = parseSalary(values.post_trial_salary);

    return {
      job_title: values.job_title,
      organisation_id: values.organisation,
      department_id: values.department,
      city: values.city,
      author_name: values.author,
      creator_position: values.author_position,
      assigner_id: values.assignee_id,
      count: values.positions_count,
      rate: values.rate,
      vacancy_reason: values.vacancy_reason,
      subordinate_count: values.subordinates_count,
      job_address: values.workplace_address,
      is_hidden: values.is_hidden || false,
      visible_user_ids: values.visible_user_ids || [],
      opening_date: values.submission_date ? moment(values.submission_date, 'YYYY-MM-DD') : null,
      planned_close_date: values.planned_close_date ? moment(values.planned_close_date, 'YYYY-MM-DD').startOf('day') : null,
      planned_start_date: values.planned_start_date ? values.planned_start_date : null,
      work_start_time: values.work_start_time ? moment(values.work_start_time, 'HH:mm') : null,
      work_end_time: values.work_end_time ? moment(values.work_end_time, 'HH:mm') : null,
      work_schedule: values.work_schedule,
      work_location_ratio: values.field_vs_office,
      business_trips_required: values.requires_travel,
      business_trips_details: values.travel_details,
      relocation_willingness: values.requires_relocation,
      communication_compensation: values.compensate_phone,
      car_compensation: values.compensate_car,
      driver_license_required: values.requires_license,
      driver_license_categories: values.license_categories,
      tasks: values.tasks,
      expected_results: values.expected_results,
      responsibilities: values.functional_responsibilities,
      reporting: values.reporting_details,
      net_probation_salary_min: probationSalary.min,
      net_probation_salary_max: probationSalary.max,
      gross_probation_salary_min: probationSalary.min,
      gross_probation_salary_max: probationSalary.max,
      net_post_probation_salary_min: postProbationSalary.min,
      net_post_probation_salary_max: postProbationSalary.max,
      gross_post_probation_salary_min: postProbationSalary.min,
      gross_post_probation_salary_max: postProbationSalary.max,
      salary_structure: values.salary_structure,
      supervisors: values.supervisors,
      is_material_responsible: values.is_material_responsible,
      gender: values.gender,
      min_age: values.age,
      max_age: values.age,
      marital_status: values.marital_status,
      has_car: values.requires_car,
      education: values.education_levels || [],
      pc_skills: values.computer_skills_level,
      skills_1c: values.skills_1c,
      software_knowledge: values.software_skills || [],
      language_knowledge: values.language_skills || [],
      work_experience: values.experience_description,
      personal_qualities: values.personal_traits,
      professional_skills: values.professional_skills,
      preferred_companies: values.preferred_companies,
      unwanted_companies: values.unwanted_companies,
      other_conditions: values.other_conditions
    };
  };

  const handleCompanyChange = (orgId) => {
    form.setFieldsValue({ department_id: undefined, assigner_id: undefined });
    filterDepartments(orgId);
  };

  useEffect(() => {
    if (selectedOrganisationId) {
      dispatch(fetchUsersByOrganisation(selectedOrganisationId));
    } else {
      dispatch(clearOrganisationUsers());
    }
  }, [selectedOrganisationId, dispatch]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      // Маппинг полей формы на поля API
      const apiData = {
        job_title: values.job_title,
        organisation_id: values.organisation_id,
        department_id: values.department_id,
        city: values.city,
        author: Number(user.id),
        author_position: values.creator_position,
        assigner_id: values.assigner_id,
        positions_count: values.count,
        rate: values.rate,
        vacancy_reason: values.vacancy_reason,
        subordinates_count: values.subordinate_count ? String(values.subordinate_count) : '0',
        workplace_address: values.job_address,
        is_hidden: isHiddenVacancy,
        visible_user_ids: isHiddenVacancy ? (values.visible_user_ids || []) : [],
        open_date: values.opening_date ? values.opening_date.format('YYYY-MM-DD') : null,
        planned_close_date: values.planned_close_date ? values.planned_close_date.format('YYYY-MM-DD') : null,
        planned_start_date: values.planned_start_date ? values.planned_start_date : null,
        work_start_time: values.work_start_time ? values.work_start_time.format('HH:mm') : null,
        work_end_time: values.work_end_time ? values.work_end_time.format('HH:mm') : null,
        work_schedule: values.work_schedule,
        field_vs_office: values.work_location_ratio,
        requires_travel: businessTripsRequired,
        travel_details: values.business_trips_details,
        requires_relocation: relocationWillingness,
        compensate_phone: communicationCompensation,
        compensate_car: carCompensationValue,
        requires_license: driverLicenseRequiredValue,
        license_categories: values.driver_license_categories || [],
        tasks: values.tasks,
        expected_results: values.expected_results,
        functional_responsibilities: values.responsibilities,
        reporting_details: values.reporting,
        trial_salary: values.net_probation_salary_min || values.net_probation_salary_max
          ? `${values.net_probation_salary_min || ''}${values.net_probation_salary_min && values.net_probation_salary_max ? '-' : ''}${values.net_probation_salary_max || ''}`
          : '',
        post_trial_salary: values.net_post_probation_salary_min || values.net_post_probation_salary_max
          ? `${values.net_post_probation_salary_min || ''}${values.net_post_probation_salary_min && values.net_post_probation_salary_max ? '-' : ''}${values.net_post_probation_salary_max || ''}`
          : '',
        salary_structure: values.salary_structure,
        supervisors: values.supervisors,
        is_material_responsible: values.is_material_responsible,
        gender: values.gender,
        age: values.min_age ? String(values.min_age) : '',
        marital_status: values.marital_status,
        requires_car: hasCar,
        education_levels: values.education || [],
        computer_skills_level: values.pc_skills,
        skills_1c: values.skills_1c,
        software_skills: (values.software_knowledge || [])
          .filter(sk => sk && sk.program && sk.program.trim() !== '')
          .map(sk => ({
            program: sk.program.trim(),
            level: sk.level || 'Не требуется'
          })),
        language_skills: (values.language_knowledge || [])
          .filter(lang => lang && lang.language && lang.language.trim() !== '')
          .map(lang => ({
            language: lang.language.trim(),
            level: lang.level || 'Не требуется'
          })),
        experience_description: values.work_experience,
        personal_traits: values.personal_qualities,
        professional_skills: values.professional_skills,
        preferred_companies: values.preferred_companies,
        unwanted_companies: values.unwanted_companies,
        other_conditions: values.other_conditions,
        status: 'new',
        is_template: false
      };

      await onSubmit(apiData);
      onClose();
    } catch (error) {
      if (error?.errorFields?.length > 0) {
        form.scrollToField(error.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setDraftLoading(true);
      const values = await form.validateFields();

      // Маппинг полей формы на поля API для черновика
      const apiData = {
        job_title: values.job_title,
        organisation_id: values.organisation_id,
        department_id: values.department_id,
        city: values.city,
        author: Number(user.id),
        author_position: values.creator_position,
        assigner_id: values.assigner_id,
        positions_count: values.count,
        rate: values.rate,
        vacancy_reason: values.vacancy_reason,
        subordinates_count: values.subordinate_count ? String(values.subordinate_count) : '0',
        workplace_address: values.job_address,
        is_hidden: isHiddenVacancy,
        visible_user_ids: isHiddenVacancy ? (values.visible_user_ids || []) : [],
        open_date: values.opening_date ? values.opening_date.format('YYYY-MM-DD') : null,
        planned_close_date: values.planned_close_date ? values.planned_close_date.format('YYYY-MM-DD') : null,
        planned_start_date: values.planned_start_date ? values.planned_start_date : null,
        work_start_time: values.work_start_time ? values.work_start_time.format('HH:mm') : null,
        work_end_time: values.work_end_time ? values.work_end_time.format('HH:mm') : null,
        work_schedule: values.work_schedule,
        field_vs_office: values.work_location_ratio,
        requires_travel: businessTripsRequired,
        travel_details: values.business_trips_details,
        requires_relocation: relocationWillingness,
        compensate_phone: communicationCompensation,
        compensate_car: carCompensationValue,
        requires_license: driverLicenseRequiredValue,
        license_categories: values.driver_license_categories || [],
        tasks: values.tasks,
        expected_results: values.expected_results,
        functional_responsibilities: values.responsibilities,
        reporting_details: values.reporting,
        trial_salary: values.net_probation_salary_min || values.net_probation_salary_max
          ? `${values.net_probation_salary_min || ''}${values.net_probation_salary_min && values.net_probation_salary_max ? '-' : ''}${values.net_probation_salary_max || ''}`
          : '',
        post_trial_salary: values.net_post_probation_salary_min || values.net_post_probation_salary_max
          ? `${values.net_post_probation_salary_min || ''}${values.net_post_probation_salary_min && values.net_post_probation_salary_max ? '-' : ''}${values.net_post_probation_salary_max || ''}`
          : '',
        salary_structure: values.salary_structure,
        supervisors: values.supervisors,
        is_material_responsible: values.is_material_responsible,
        gender: values.gender,
        age: values.min_age ? String(values.min_age) : '',
        marital_status: values.marital_status,
        requires_car: hasCar,
        education_levels: values.education || [],
        computer_skills_level: values.pc_skills,
        skills_1c: values.skills_1c,
        software_skills: (values.software_knowledge || [])
          .filter(sk => sk && sk.program && sk.program.trim() !== '')
          .map(sk => ({
            program: sk.program.trim(),
            level: sk.level || 'Не требуется'
          })),
        language_skills: (values.language_knowledge || [])
          .filter(lang => lang && lang.language && lang.language.trim() !== '')
          .map(lang => ({
            language: lang.language.trim(),
            level: lang.level || 'Не требуется'
          })),
        experience_description: values.work_experience,
        personal_traits: values.personal_qualities,
        professional_skills: values.professional_skills,
        preferred_companies: values.preferred_companies,
        unwanted_companies: values.unwanted_companies,
        other_conditions: values.other_conditions,
        status: 'new',
        is_template: true
      };

      await onSubmit(apiData);
      setIsDraftSaved(true);
      onClose();
    } catch (error) {
      console.error('Draft save failed:', error);
      if (error?.errorFields?.length > 0) {
        form.scrollToField(error.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center'
        });
      }
    } finally {
      setDraftLoading(false);
    }
  };

  const handleDeleteDraft = () => {
    setIsDraftSaved(false);
    form.resetFields();
    onClose();
  };

  const footerButtons = [
    <Button key="cancel" onClick={onClose}>Закрыть</Button>,
    !isDraftSaved ? (
      <Button key="save-draft" onClick={handleSaveDraft} loading={draftLoading}>
        Сохранить как черновик
      </Button>
    ) : (
      <Button key="delete-draft" danger onClick={handleDeleteDraft}>
        Удалить
      </Button>
    ),
    <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
      Создать вакансию
    </Button>
  ];

  if (!isOpen) return null;

  return (
    <>
      <Modal
        title={initialValues ? "Создание по заявке" : "Создать вакансию"}
        open={isOpen}
        onCancel={handleModalCancel}
        footer={footerButtons}
        width={1000}
      >
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={handleFormChange}
        >
          {/* Основная информация о вакансии */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Основная информация
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="job_title" label="Название должности" rules={[{ required: true, message: 'Укажите название должности' }]}>
                  <Input />
                </Form.Item>

                <Form.Item name="organisation_id" label="Компания" rules={[{ required: true, message: 'Выберите компанию' }]}>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder="Выберите компанию"
                    onChange={handleCompanyChange}
                  >
                    {organisations?.map(org => (
                      <Option key={org.id} value={org.organisation_id}>{org.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="department_id" label="Отдел">
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder="Выберите отдел"
                  >
                    {filteredDepartments?.map(dept => (
                      <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="city" label="Город">
                  <Input />
                </Form.Item>

                <Form.Item name="job_address" label="Адрес рабочего места">
                  <Input />
                </Form.Item>

                <Form.Item name="is_hidden" valuePropName="checked" label="Скрытая вакансия">
                  <Button
                    type={isHiddenVacancy ? "primary" : "default"}
                    onClick={() => setIsHiddenVacancy(!isHiddenVacancy)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: isHiddenVacancy ? 'bold' : 'normal'
                    }}
                  >
                    {isHiddenVacancy ? '✓ Скрытая вакансия' : 'Скрытая вакансия'}
                  </Button>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="count" label="Количество позиций">
                  <InputNumber min={1} max={10} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="rate" label="Ставка">
                  <Select placeholder="Выберите ставку">
                    {['0.25', '0.5', '0.75', '1', '1.25', '1.5', '1.75', '2'].map(value => (
                      <Option key={value} value={value}>
                        {value}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="vacancy_reason" label="Причина возникновения вакансии">
                  <Select>
                    {vacancyReasons.map(reason => (
                      <Option key={reason} value={reason}>{reason}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="subordinate_count" label="Подчиненные (количество)">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="planned_close_date" label="Планируемая дата закрытия вакансии">
                  <DatePicker
                    style={{ width: '100%' }}
                    showTime={false}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>

                {isHiddenVacancy && (
                  <Form.Item
                    name="visible_user_ids"
                    label="Пользователи, которые смогут видеть вакансию"
                    rules={[
                      {
                        required: isHiddenVacancy,
                        message: 'Выберите хотя бы одного пользователя'
                      }
                    ]}
                  >
                    <Select
                      mode="multiple"
                      showSearch
                      optionFilterProp="children"
                      placeholder="Выберите пользователей"
                      style={{ width: '100%' }}
                    >
                      {users?.map(user => (
                        <Option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
            </Row>
          </div>

          {/* Ответственные лица */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Ответственные лица
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="author_name" label="Автор заявки (ФИО)">
                  <Input readOnly />
                </Form.Item>

                <Form.Item name="creator_position" label="Автор заявки (должность)">
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="assigner_id" label="Рекрутер">
                  <Select showSearch optionFilterProp="children">
                    {selectedOrganisationId && organisationUsers?.length > 0
                      ? (() => {
                        // Фильтруем уникальных пользователей по user_id
                        const uniqueUsers = [];
                        const seenUserIds = new Set();
                        organisationUsers.forEach(user => {
                          const userId = user.user_id || user.id;
                          if (!seenUserIds.has(userId)) {
                            seenUserIds.add(userId);
                            uniqueUsers.push(user);
                          }
                        });
                        return uniqueUsers.map(user => (
                          <Option key={user.user_id || user.id} value={user.user_id || user.id}>
                            {user.first_name} {user.last_name}
                          </Option>
                        ));
                      })()
                      : users?.map(user => (
                        <Option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </Option>
                      ))
                    }
                  </Select>
                </Form.Item>

                <Form.Item name="supervisors" label="Начальник">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Даты и время */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Даты и время
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="opening_date" label="Дата открытия вакансии">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="planned_start_date" label="Планируемая дата выхода сотрудника на работу">
                  {/* <DatePicker
                    style={{ width: '100%' }}
                  /> */}
                  <Input placeholder="Можно указать дату, месяц или период (например: 01.09.2023, сентябрь 2023, 3-й квартал 2023)" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="work_start_time" label="Время начала работы">
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    needConfirm={false}
                    placeholder="Выберите время"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="work_end_time" label="Время окончания работы">
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    needConfirm={false}
                    placeholder="Выберите время"
                  />
                </Form.Item>
              </Col>


            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="work_schedule" label="График работы">
                  <Select
                    options={workSchedules.map(schedule => ({ value: schedule, label: schedule }))}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="work_location_ratio" label="Соотношение работы: дом/офис">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Условия работы */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Условия работы
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="communication_compensation" valuePropName="checked" label="Компенсация затрат: связь">
                  <Button
                    type={communicationCompensation ? "primary" : "default"}
                    onClick={() => setCommunicationCompensation(!communicationCompensation)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: communicationCompensation ? 'bold' : 'normal'
                    }}
                  >
                    {communicationCompensation ? '✓ Компенсация затрат: связь' : 'Компенсация затрат: связь'}
                  </Button>
                </Form.Item>

                {/* Компенсация автомобиля */}
                <Form.Item name="car_compensation" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Компенсация затрат: личный автомобиль">
                  <Button
                    type={carCompensationValue ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('car_compensation')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: carCompensationValue ? 'bold' : 'normal'
                    }}
                  >
                    {carCompensationValue ? '✓ Компенсация затрат: личный автомобиль' : 'Компенсация затрат: личный автомобиль'}
                  </Button>
                </Form.Item>

                {/* <Form.Item name="is_material_responsible" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Материально ответственное лицо">
                  <Button
                    type={isMaterialResponsibleValue ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('is_material_responsible')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: isMaterialResponsibleValue ? 'bold' : 'normal'
                    }}
                  >
                    {isMaterialResponsibleValue ? '✓ Материально ответственное лицо' : 'Материально ответственное лицо'}
                  </Button>
                </Form.Item> */}
              </Col>

              <Col span={12}>
                <Form.Item name="business_trips_required" valuePropName="checked" label="Командировки">
                  <Button
                    type={businessTripsRequired ? "primary" : "default"}
                    onClick={() => setBusinessTripsRequired(!businessTripsRequired)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: businessTripsRequired ? 'bold' : 'normal'
                    }}
                  >
                    {businessTripsRequired ? '✓ Командировки' : 'Командировки'}
                  </Button>
                </Form.Item>

                {businessTripsRequired && (
                  <Form.Item name="business_trips_details" label="Частота и длительность командировок">
                    <Input />
                  </Form.Item>
                )}

                <Form.Item name="relocation_willingness" valuePropName="checked" label="Готовность к переезду">
                  <Button
                    type={relocationWillingness ? "primary" : "default"}
                    onClick={() => setRelocationWillingness(!relocationWillingness)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: relocationWillingness ? 'bold' : 'normal'
                    }}
                  >
                    {relocationWillingness ? '✓ Готовность к переезду' : 'Готовность к переезду'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Описание работы */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Описание работы
            </h3>
            <Form.Item name="tasks" label="Задачи">
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="expected_results" label="Ожидаемые результаты">
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="responsibilities" label="Функциональные обязанности">
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="reporting" label="Отчетность о проделанной работе">
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {/* Зарплата */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Зарплата
            </h3>
            <Form.Item name="salary_structure" label="Структура заработной платы">
              <TextArea rows={2} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Зарплата на испытательном сроке (BYN)"
                  validateStatus={form.getFieldError('net_probation_salary_min')?.length > 0 || form.getFieldError('net_probation_salary_max')?.length > 0 ? 'error' : ''}
                  help={form.getFieldError('net_probation_salary_min')?.[0] || form.getFieldError('net_probation_salary_max')?.[0]}
                >
                  <Input.Group compact>
                    <Form.Item name="net_probation_salary_min" noStyle rules={getValidationRules('net_probation_salary_min', false)}>
                      <InputNumber
                        min={0}
                        placeholder="От"
                        style={{ width: '50%' }}
                        parser={value => value?.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                    <Form.Item name="net_probation_salary_max" noStyle rules={getValidationRules('net_probation_salary_max', false)}>
                      <InputNumber
                        min={0}
                        placeholder="До"
                        style={{ width: '50%' }}
                        parser={value => value?.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Зарплата после испытательного срока (BYN)"
                  validateStatus={form.getFieldError('net_post_probation_salary_min')?.length > 0 || form.getFieldError('net_post_probation_salary_max')?.length > 0 ? 'error' : ''}
                  help={form.getFieldError('net_post_probation_salary_min')?.[0] || form.getFieldError('net_post_probation_salary_max')?.[0]}
                >
                  <Input.Group compact>
                    <Form.Item name="net_post_probation_salary_min" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="От"
                        style={{ width: '50%' }}
                        parser={value => value?.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                    <Form.Item name="net_post_probation_salary_max" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="До"
                        style={{ width: '50%' }}
                        parser={value => value?.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Требования к кандидату */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Требования к кандидату
            </h3>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="gender" label="Пол">
                  <Select>
                    <Option value="Мужской">Мужской</Option>
                    <Option value="Женский">Женский</Option>
                    <Option value="Не имеет значения">Не имеет значения</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="min_age" label="Минимальный возраст">
                  <InputNumber min={18} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="max_age" label="Максимальный возраст">
                  <InputNumber min={18} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="marital_status" label="Семейное положение">
                  <Select
                    mode="multiple"
                    options={[
                      { value: 'Холост/Не замужем', label: 'Холост/Не замужем' },
                      { value: 'Женат/Замужем', label: 'Женат/Замужем' },
                      { value: 'Разведен(а)', label: 'Разведен(а)' },
                      { value: 'Вдовец/Вдова', label: 'Вдовец/Вдова' },
                      { value: 'Не имеет значения', label: 'Не имеет значения' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="education" label="Образование">
                  <Select
                    mode="multiple"
                    options={[
                      'Базовое',
                      'Среднее',
                      'Профессионально-техническое',
                      'Среднее специальное',
                      'Высшее'
                    ].map(ed => ({ value: ed, label: ed }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                {/* Водительское удостоверение */}
                <Form.Item name="driver_license_required" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Наличие водительского удостоверения">
                  <Button
                    type={driverLicenseRequiredValue ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('driver_license_required')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: driverLicenseRequiredValue ? 'bold' : 'normal'
                    }}
                  >
                    {driverLicenseRequiredValue ? '✓ Наличие водительского удостоверения' : 'Наличие водительского удостоверения'}
                  </Button>
                </Form.Item>

                {driverLicenseRequiredValue && (
                  <Form.Item name="driver_license_categories" label="Категории водительских прав">
                    <Select
                      mode="multiple"
                      options={[
                        'A', 'A1', 'B', 'B1', 'C', 'C1', 'D', 'D1', 'BE', 'CE', 'C1E', 'DE', 'D1E'
                      ].map(cat => ({ value: cat, label: cat }))}
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                <Form.Item name="has_car" valuePropName="checked" label="Наличие личного автомобиля">
                  <Button
                    type={hasCar ? "primary" : "default"}
                    onClick={() => setHasCar(!hasCar)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: hasCar ? 'bold' : 'normal'
                    }}
                  >
                    {hasCar ? '✓ Наличие личного автомобиля' : 'Наличие личного автомобиля'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Навыки и опыт */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Навыки и опыт
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="pc_skills" label="Уровень владения ПК (Word, Excel и другие)">
                  <Select
                    options={[
                      'Не требуется',
                      'Базовый',
                      'Уверенный',
                      'Продвинутый'
                    ].map(level => ({ value: level, label: level }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="skills_1c" label="Навыки в работе с 1С">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.List name="software_knowledge">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'program']} label="Программа">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                          <Select options={[
                            'Базовый',
                            'Уверенный',
                            'Продвинутый'
                          ].map(l => ({ value: l, label: l }))} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                        <Button danger onClick={() => remove(name)}>Удалить</Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      Добавить программу
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.List name="language_knowledge">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'language']} label="Язык">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                          <Select options={[
                            'Базовый',
                            'Уверенный',
                            'Продвинутый'
                          ].map(l => ({ value: l, label: l }))} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                        <Button danger onClick={() => remove(name)}>Удалить</Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      Добавить язык
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item name="work_experience" label="Опыт работы (область, продолжительность)">
              <TextArea rows={2} />
            </Form.Item>
          </div>

          {/* Личные и профессиональные качества */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Личные и профессиональные качества
            </h3>
            <Form.Item name="personal_qualities" label="Личные качества кандидатов">
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="professional_skills" label="Профессиональные навыки кандидатов">
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {/* Дополнительные требования */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Дополнительные требования
            </h3>
            <Form.Item name="preferred_companies" label="Компании, из которых предпочтительно рассматривать кандидатов">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item name="unwanted_companies" label="Компании, из которых нежелательно рассматривать кандидатов">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item name="other_conditions" label="Другие условия">
              <TextArea rows={3} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <ConfirmModal
        visible={showConfirm}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirm(false)}
        title="Подтверждение закрытия"
        message="Данные не будут сохранены. Продолжить?"
        okText="Продолжить"
        cancelText="Отмена"
        type="warning"
      />
    </>
  );
};

export default CreateModal;